import React from 'react';
import { Link } from 'react-router-dom';
import LandingIntroVideo from './LandingIntroVideo';
import LandingTestimonials from './LandingTestimonials';
import LandingInteractiveDemoSection from './LandingInteractiveDemoSection';
import LandingSignupSection from './LandingSignupSection';
import LandingPwaInstallSection from './LandingPwaInstallSection';
import LandingProductSteps, { LandingCircleImage, LandingStartAction } from './LandingProductSteps';
import './landingCareStory.css';

const TRUST = [
  [
    'You review what matters',
    'Lifelog helps organize your notes. Review and confirm details that need a decision, including medication changes.',
  ],
  ['Sources stay connected', 'Return to the note, photo or document behind an organized detail.'],
  [
    'Support, not medical advice',
    'Lifelog helps you organize and understand care information. It doesn’t replace medical or professional judgment.',
  ],
];
const JOURNEYS = [
  [
    'Neurodivergence & developmental support',
    'behaviors, routines, school, therapies and the strategies that help, connected over time.',
    '/autism-support',
  ],
  [
    'Aging & memory care',
    'changes in daily life, medications, mobility and safety, shared across family and caregivers.',
    '/elder-care',
  ],
  [
    'Health & complex care',
    'symptoms, medications, documents and appointments in one continuous record.',
    '/family-care',
  ],
];
const FOOTER_LINKS = [
  ['How it works', '/how-it-works'],
  ['Privacy Policy', '/privacy-policy'],
  ['Cookie Policy', '/cookie-policy'],
  ['Terms of Service', '/terms-of-service'],
  ['Contact', '/contact-us'],
];

const LandingCareStoryHome = () => (
  <div className="care-landing">
    <section className="care-landing-hero care-landing-container" data-cy="landing-hero">
      <div className="care-landing-hero-copy">
        <span className="care-landing-eyebrow">Care that remembers</span>
        <h1 className="care-landing-headline">
          <span>Caregiving is a lot to remember.</span>{' '}
          <span>Lifelog remembers with you.</span>
        </h1>
        <p className="care-landing-lede">
          A place for the everyday details of caring for someone you love. Keep them together,
          so they’re there when you need them.
        </p>
        <LandingStartAction />
        <a
          className="care-landing-text-link"
          href="#landing-interactive-demo"
          data-cy="landing-start-tracking-btn"
        >
          Try Maya’s story →
        </a>
      </div>
      <img
        className="care-landing-hero-screen"
        src="/media/landing/care-story.png"
        alt="Maya’s care story, with who she is, her harder moments, and what helps"
      />
    </section>
    <section className="care-landing-section care-landing-container">
      <div className="care-landing-founder">
        <div className="care-landing-founder-avatar" aria-hidden="true">
          K
        </div>
        <div>
          <span className="care-landing-eyebrow">Why I built Lifelog</span>
          <h2>From one caregiver to another.</h2>
          <p>
            I built Lifelog from my own experience caring for people I love. I wanted a place for
            the details that are easy to forget between appointments, and the small things that help
            a day go better.
          </p>
          <p>My hope is that it gives you a little more room to simply be there.</p>
          <p className="care-landing-founder-signature">
            <strong>Kalyani</strong>
            <br />
            Founder · Caregiver
          </p>
        </div>
      </div>
    </section>
    <LandingTestimonials />
    <LandingIntroVideo />
    <LandingInteractiveDemoSection />
    <LandingProductSteps />
    <section id="care-journeys" className="care-landing-section care-landing-container">
      <div className="care-landing-section-heading">
        <span className="care-landing-eyebrow">Care is shared</span>
        <h2>There’s room for the people who care with you.</h2>
        <p>
          Invite family, home aides, therapists or teachers when you’re ready. You choose who can
          view the record and who can contribute.
        </p>
      </div>
      <div className="care-landing-feature-grid">
        <LandingCircleImage />
        <div className="care-landing-card care-landing-journeys">
          <h3>Who it’s for</h3>
          {JOURNEYS.map(([title, description, to]) => (
            <p key={to}>
              <Link to={to}>{title}</Link> — {description}
            </p>
          ))}
        </div>
      </div>
      <Link className="care-landing-text-link care-landing-story-link" to="/how-it-works">
        See how two siblings share their dad’s care →
      </Link>
    </section>
    <LandingSignupSection />
    <LandingPwaInstallSection />
    <section id="trust" className="care-landing-section care-landing-container">
      <div className="care-landing-section-heading">
        <span className="care-landing-eyebrow">Built around caregiver control</span>
        <h2>Your care story. Your control.</h2>
      </div>
      <div className="care-landing-cards">
        {TRUST.map(([title, text]) => (
          <div className="care-landing-card" key={title}>
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </section>
    <section className="care-landing-section care-landing-container">
      <div className="care-landing-close">
        <h2>Start wherever you are.</h2>
        <p>You don’t need to catch up on everything. One note about today is enough to begin.</p>
        <LandingStartAction />
      </div>
    </section>
    <footer className="care-landing-footer care-landing-container" data-cy="landing-footer">
      <Link className="care-landing-wordmark" to="/">
        lifelog
      </Link>
      <nav aria-label="Footer">
        {FOOTER_LINKS.map(([label, to]) => (
          <Link key={to} to={to}>
            {label}
          </Link>
        ))}
      </nav>
      <p>Lifelog © {new Date().getFullYear()}</p>
    </footer>
  </div>
);
export default LandingCareStoryHome;
